import { forwardRef, HTMLAttributes, Ref, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { IComponent } from '../../models/components.models';
import { useClassNames } from '../../utils/hooks';
import { IPaginationStyles, paginationStylesDefault } from './Pagination.styles';

export enum PaginationType {
  circle = 'circle',
}

interface IPaginationClassNames {
  root: string;
  items: string;
  item: string;
  itemText: string;
}

export interface IPaginationProps
  extends IComponent<IPaginationStyles>,
    Omit<HTMLAttributes<HTMLDivElement>, 'onClick'> {
  type?: PaginationType;
  selectedPage?: number;
  count: number;
  onClick?: (page: number) => void;
}

export const Pagination = forwardRef((props: IPaginationProps, ref: Ref<HTMLDivElement>) => {
  const { className, styles, type = PaginationType.circle, selectedPage = 1, count, onClick, ...elementProps } = props;
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const refRoot = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => refRoot.current as HTMLDivElement);
  const classNames = useClassNames<IPaginationStyles, IPaginationClassNames>(
    'Pagination',
    paginationStylesDefault,
    styles,
    className
  );
  const onClickItem = (index: number) => {
    index = getValidIndex(index);
    setSelectedIndex(index);
    if (onClick) {
      onClick(index + 1);
    }
  };

  const getValidIndex = (index: number) => {
    const lastIndex = count - 1;
    return index < 0 ? 0 : index > lastIndex ? lastIndex : index;
  };

  useEffect(() => {
    const index = selectedPage - 1;
    setSelectedIndex(index);
    const itemEl = refRoot.current!.querySelector(`.h2o-Pagination-item[data-index="${index}"]`) as HTMLElement;
    if (itemEl) {
      itemEl.focus();
    }
  }, [selectedPage]);

  return (
    <div {...elementProps} className={classNames.root} ref={refRoot} data-test={props['data-test'] || 'pagination'}>
      <div className={classNames.items}>
        {[...Array(count).keys()].map((index) => (
          <button
            key={index}
            className={`${classNames.item} ${type} ${index === selectedIndex ? 'selected' : ''}`}
            data-index={index}
            onClick={() => onClickItem(index)}
          >
            <div className={classNames.itemText}>{index}</div>
          </button>
        ))}
      </div>
    </div>
  );
});
